import React, { useEffect, useState, useRef, memo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { playerService } from '../../../../services/player';
import { getImageAnalysis2 } from '../../../../utils/imageAnyliser';
import tinycolor from 'tinycolor2';
import { spotifyActions } from '../../../../store/slices/spotify';
import { Marquee } from './Marquee';
import MobileNowPlayingModal from './MobileNowPlayingModal';

const NowPlayingBarMobile = () => {
  const dispatch = useAppDispatch();
  const position = useAppSelector((state) => state.spotify.state?.position || 0);
  const duration = useAppSelector((state) => state.spotify.state?.duration || 1);
  const currentSong = useAppSelector(
    (state) => state.spotify.state?.track_window.current_track,
    (a, b) => a?.id === b?.id
  );
  const nextSong = useAppSelector(
    (state) => state.spotify.state?.track_window.next_tracks?.[0] || state.queue.queue?.[0]
  );
  const prevSong = useAppSelector((state) => {
    const hist = state.spotify.state?.track_window.previous_tracks;
    return hist && hist.length > 0 ? hist[hist.length - 1] : null;
  });
  const paused = useAppSelector((state) => state.spotify.state?.paused ?? true);
  const liked = useAppSelector((state) => state.spotify.liked);
  const [currentColor, setColor] = useState('#2d261e');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Swipe gesture tracking & smooth slide carousel
  const viewportRef = useRef<HTMLDivElement>(null);
  const [dragX, setDragX] = useState(0);
  const [transitionStyle, setTransitionStyle] = useState<string>('none');
  const dragStartX = useRef<number>(0);
  const dragStartY = useRef<number>(0);
  const dragStartTime = useRef<number>(0);
  const isDragging = useRef<boolean>(false);
  const isHorizontalSwipe = useRef<boolean>(false);
  const swipedRecently = useRef<boolean>(false);
  const handledByPointer = useRef<boolean>(false);
  const animatingTarget = useRef<'next' | 'prev' | null>(null);

  // Measure full slide distance (viewport width + gap)
  const getSlideDistance = () => {
    const width = viewportRef.current?.clientWidth || 220;
    return width + 24;
  };

  // When track actually changes in Redux, seamlessly complete the transition
  const currentSongId = currentSong?.id;
  useEffect(() => {
    if (animatingTarget.current) {
      // Redux has updated to the new track: snap to center with no transition (zero glitch)
      setTransitionStyle('none');
      setDragX(0);
      animatingTarget.current = null;
      setTimeout(() => {
        swipedRecently.current = false;
        isHorizontalSwipe.current = false;
      }, 150);
    }
  }, [currentSongId]);

  useEffect(() => {
    if (currentSong && currentSong.album?.images?.[0]?.url) {
      getImageAnalysis2(currentSong.album.images[0].url).then((r) => {
        let color = tinycolor(r);
        while (color.isLight()) {
          color = color.darken(8);
        }
        if (color.getBrightness() > 95) {
          color = color.darken(15);
        }
        setColor(color.toHexString());
      });
    }
  }, [currentSong]);

  if (!currentSong) return null;

  const coverUrl = currentSong.album?.images?.[0]?.url || '';
  const artistsList = currentSong.artists?.map((a: any) => a.name).join(', ') || '';
  const progressPercent = Math.min(100, Math.max(0, (position / duration) * 100));

  const startDrag = (clientX: number, clientY: number) => {
    if (animatingTarget.current) return;
    dragStartX.current = clientX;
    dragStartY.current = clientY;
    dragStartTime.current = Date.now();
    isDragging.current = true;
    isHorizontalSwipe.current = false;
    setTransitionStyle('none');
  };

  const moveDrag = (clientX: number, clientY: number) => {
    if (!isDragging.current || animatingTarget.current) return;
    const deltaX = clientX - dragStartX.current;
    const deltaY = clientY - dragStartY.current;

    // Detect horizontal swipe intent once movement > 7px and more horizontal than vertical
    if (!isHorizontalSwipe.current && Math.abs(deltaX) > 7 && Math.abs(deltaX) > Math.abs(deltaY)) {
      isHorizontalSwipe.current = true;
    }

    if (isHorizontalSwipe.current) {
      const distance = getSlideDistance();
      if (deltaX < 0) {
        // Swiping Left (Next track) - 1:1 finger tracking across full viewport
        if (Math.abs(deltaX) <= distance) {
          setDragX(deltaX);
        } else {
          const over = Math.abs(deltaX) - distance;
          setDragX(-distance - Math.pow(over, 0.75) * 1.5);
        }
      } else {
        // Swiping Right (Previous track) - 1:1 finger tracking across full viewport
        if (deltaX <= distance) {
          setDragX(deltaX);
        } else {
          const over = deltaX - distance;
          setDragX(distance + Math.pow(over, 0.75) * 1.5);
        }
      }
    }
  };

  const endDrag = (clientX: number) => {
    if (!isDragging.current || animatingTarget.current) return;
    isDragging.current = false;

    if (!isHorizontalSwipe.current) {
      setDragX(0);
      return;
    }

    const deltaX = clientX - dragStartX.current;
    const distance = getSlideDistance();
    const elapsed = Math.max(1, Date.now() - dragStartTime.current);
    const velocity = Math.abs(deltaX) / elapsed; // px/ms

    // Threshold: swiped > 28% of distance OR flick with velocity > 0.35
    const isCommitted = Math.abs(deltaX) > distance * 0.28 || (velocity > 0.35 && Math.abs(deltaX) > 24);

    if (isCommitted && deltaX < 0) {
      // Full smooth slide to NEXT track
      swipedRecently.current = true;
      animatingTarget.current = 'next';

      const remaining = Math.abs(-distance - dragX);
      const durationSec = Math.min(0.28, Math.max(0.18, remaining / 650));
      setTransitionStyle(`transform ${durationSec.toFixed(2)}s cubic-bezier(0.22, 1, 0.36, 1)`);
      setDragX(-distance);

      // Trigger next track playback
      setTimeout(() => {
        playerService.nextTrack();
      }, 60);

      // Safety timeout in case track transition fails or takes too long
      setTimeout(() => {
        if (animatingTarget.current === 'next') {
          setTransitionStyle('none');
          setDragX(0);
          animatingTarget.current = null;
          swipedRecently.current = false;
          isHorizontalSwipe.current = false;
        }
      }, 450);

    } else if (isCommitted && deltaX > 0) {
      // Full smooth slide to PREVIOUS track
      swipedRecently.current = true;
      animatingTarget.current = 'prev';

      const remaining = Math.abs(distance - dragX);
      const durationSec = Math.min(0.28, Math.max(0.18, remaining / 650));
      setTransitionStyle(`transform ${durationSec.toFixed(2)}s cubic-bezier(0.22, 1, 0.36, 1)`);
      setDragX(distance);

      // Trigger previous track playback
      setTimeout(() => {
        playerService.previousTrack();
      }, 60);

      // Safety timeout
      setTimeout(() => {
        if (animatingTarget.current === 'prev') {
          setTransitionStyle('none');
          setDragX(0);
          animatingTarget.current = null;
          swipedRecently.current = false;
          isHorizontalSwipe.current = false;
        }
      }, 450);

    } else {
      // Cancelled swipe: spring smoothly back to center
      setTransitionStyle('transform 0.24s cubic-bezier(0.22, 1, 0.36, 1)');
      setDragX(0);
      setTimeout(() => {
        swipedRecently.current = false;
        isHorizontalSwipe.current = false;
      }, 240);
    }
  };

  const cancelDrag = () => {
    if (!isDragging.current || animatingTarget.current) return;
    isDragging.current = false;
    setTransitionStyle('transform 0.22s cubic-bezier(0.22, 1, 0.36, 1)');
    setDragX(0);
    setTimeout(() => {
      swipedRecently.current = false;
      isHorizontalSwipe.current = false;
    }, 220);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    handledByPointer.current = true;
    startDrag(e.clientX, e.clientY);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (err) {}
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    moveDrag(e.clientX, e.clientY);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (err) {}
    endDrag(e.clientX);
  };

  const handlePointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (err) {}
    cancelDrag();
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (handledByPointer.current) return;
    startDrag(e.touches[0].clientX, e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (handledByPointer.current) return;
    moveDrag(e.touches[0].clientX, e.touches[0].clientY);
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (handledByPointer.current) return;
    endDrag(e.changedTouches[0].clientX);
  };

  const handleTouchCancel = () => {
    if (handledByPointer.current) return;
    cancelDrag();
  };

  const handleCardClick = () => {
    if (swipedRecently.current || isHorizontalSwipe.current || animatingTarget.current) return;
    setIsModalOpen(true);
  };

  return (
    <>
      <div
        className='mobile-player'
        onClick={handleCardClick}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
          style={{
            backgroundColor: currentColor,
            cursor: 'pointer',
          }}
        >
          {/* Album Cover Thumbnail */}
          {coverUrl ? (
            <img
              src={coverUrl}
              alt={currentSong.name}
              className='mp-cover'
              draggable={false}
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            />
          ) : (
            <div className='mp-cover' />
          )}

          {/* Track Title & Artist Viewport (clipped so text never overlaps cover or buttons) */}
          <div className='mp-meta-viewport' ref={viewportRef}>
            <div
              className='mp-meta-track-container'
              style={{
                transform: `translateX(${dragX}px)`,
                transition: transitionStyle,
                willChange: 'transform',
              }}
            >
              {/* Previous Track (slides in from left when swiping right) */}
              <div className='mp-meta-item mp-meta-prev'>
                <div className='mp-title'>
                  {prevSong ? prevSong.name : currentSong.name}
                </div>
                <div className='mp-artist'>
                  {prevSong
                    ? ((prevSong as any).artists?.map((a: any) => a.name).join(', ') || (prevSong as any).show?.name || '')
                    : 'Replay from start'}
                </div>
              </div>

              {/* Current Track */}
              <div className='mp-meta-item mp-meta-current'>
                <div className='mp-title'>
                  <Marquee title={currentSong.name}>
                    {currentSong.name}
                  </Marquee>
                </div>
                <div className='mp-artist'>{artistsList}</div>
              </div>

              {/* Next Track (slides in from right when swiping left) */}
              <div className='mp-meta-item mp-meta-next'>
                <div className='mp-title'>
                  {nextSong ? nextSong.name : 'Next Track'}
                </div>
                <div className='mp-artist'>
                  {nextSong
                    ? ((nextSong as any).artists?.map((a: any) => a.name).join(', ') || (nextSong as any).show?.name || '')
                    : 'Autoplay'}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons: Like (Heart) & Play/Pause */}
          <div className='mp-actions'>
            <button
              className='mp-action-btn'
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                dispatch(spotifyActions.setLiked({ liked: !liked }));
              }}
              aria-label={liked ? 'Unlike' : 'Like'}
            >
              {liked ? (
                <svg role='img' height='24' width='24' viewBox='0 0 24 24' fill='#1ed760'>
                  <path d='M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z' />
                </svg>
              ) : (
                <svg role='img' height='24' width='24' viewBox='0 0 24 24' fill='none' stroke='#ffffff' strokeWidth='2'>
                  <path d='M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z' />
                </svg>
              )}
            </button>

            <button
              className='mp-action-btn'
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                if (!paused) {
                  playerService.pausePlayback();
                } else {
                  playerService.startPlayback();
                }
              }}
              aria-label={paused ? 'Play' : 'Pause'}
            >
              {paused ? (
                <svg role='img' height='24' width='24' viewBox='0 0 24 24' fill='#ffffff'>
                  <path d='M8 5v14l11-7z' />
                </svg>
              ) : (
                <svg role='img' height='24' width='24' viewBox='0 0 24 24' fill='#ffffff'>
                  <path d='M6 19h4V5H6v14zm8-14v14h4V5h-4z' />
                </svg>
              )}
            </button>
          </div>

          {/* 2px Bottom Progress Bar */}
          <div className='mp-progress-bar'>
            <div
              className='mp-progress-fill'
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

      <MobileNowPlayingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default memo(NowPlayingBarMobile);
